import React, { useCallback, useEffect, useState } from "react";
import { Investigation, Stage } from "../../lusca-search-api/generated-src";
import {
  InvestigationApiError,
  useClaimInvestigation,
  useTransferInvestigation,
} from "../../hooks/useInvestigations";
import { useToasts } from "../common/useToasts";
import { OpenInvestigationButton } from "./OpenInvestigationButton";

/**
 * "Take ownership" from a table row, shared by the overview and the registry pages. A claim moves
 * the row from "Needs an owner" into the owned table in one refetch, so without feedback it simply
 * vanishes: a toast says what happened and offers the case, and the "Just claimed" highlight marks
 * where it landed for the rest of the visit. Both reset when the stage changes.
 */
export function useTakeOwnership(stage: Stage, afterClaim?: () => unknown) {
  const claim = useClaimInvestigation(stage);
  const transfer = useTransferInvestigation(stage);
  const [claimingId, setClaimingId] = useState<string | null>(null);
  const [justClaimedIds, setJustClaimedIds] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  const toasts = useToasts();
  const { push, showError, clear } = toasts;

  useEffect(() => {
    setJustClaimedIds(new Set());
    clear();
  }, [stage, clear]);

  /**
   * Claim `inv` for the viewer, or — with `assignTo` — make that alias the owner instead (anyone
   * may assign an ownerless case to a Lusca Search user). Only the viewer's own claims get the
   * "Just claimed" highlight; an assignment moves the case to someone else's queue.
   */
  const takeOwnership = useCallback(
    (inv: Investigation, assignTo?: string) => {
      setClaimingId(inv.id);
      const request = assignTo
        ? transfer.mutateAsync({
            investigationId: inv.id,
            request: { expectedVersion: inv.version, newOwner: assignTo },
          })
        : claim.mutateAsync({
            investigationId: inv.id,
            expectedVersion: inv.version,
          });
      request
        .then(() => {
          if (!assignTo) setJustClaimedIds((ids) => new Set(ids).add(inv.id));
          push({
            type: "success",
            content: (
              <>
                {assignTo
                  ? `Assigned “${inv.title}” to ${assignTo}.`
                  : `Claimed “${inv.title}” — now under Active investigations.`}{" "}
                <OpenInvestigationButton
                  stage={stage}
                  investigationId={inv.id}
                  title={inv.title}
                />
              </>
            ),
          });
          return afterClaim?.();
        })
        .catch((err: InvestigationApiError) =>
          showError(
            assignTo
              ? `Could not assign "${inv.title}" to ${assignTo}: ${err.message}`
              : `Could not take ownership of "${inv.title}": ${err.message}`
          )
        )
        .finally(() => setClaimingId(null));
    },
    [claim, transfer, push, showError, stage, afterClaim]
  );

  return { takeOwnership, claimingId, justClaimedIds, toasts };
}
